import { GeoJson } from '@ais-ui/api-spec/shapes';
import VectorLayer from 'ol/layer/Vector';
import { shapeToFeature } from './shapeToFeature';
import VectorSource from 'ol/source/Vector';
import { getFeatureStyle } from '../styles/getStyle';
import Feature, { FeatureLike } from 'ol/Feature';
import type OlGeometry from 'ol/geom/Geometry';
import { HeatmapConfig } from '@ais-ui/basecolours';
import { feature } from '@turf/turf';

/**
 * Recursively extracts all individual features from a GeoJSON shape,
 * flattening any nested FeatureCollections into a single array of features.
 */
const extractAllFeatures = (shape: GeoJson): GeoJson[] => {
  if (shape?.type === 'FeatureCollection' && shape.features) {
    // Recursively process each feature in the collection
    return shape.features.flatMap((feature) => extractAllFeatures(feature));
  } else {
    // Individual feature (Point, LineString, Polygon, etc.)
    return [shape];
  }
};

export const shapeToVectorLayer = (
  shape: GeoJson | undefined | null,
  displayProjection: string,
  dataProjection: string
): VectorLayer<Feature<OlGeometry>> => {
  const vectorSource = new VectorSource<Feature<OlGeometry>>();

  if (shape) {
    // Extract all features recursively, handling nested FeatureCollections
    const allFeatures = extractAllFeatures(shape);

    allFeatures.forEach((featureShape) => {
      const feature = shapeToFeature(
        featureShape,
        displayProjection,
        dataProjection
      );
      if (feature?.getGeometry()) {
        vectorSource.addFeature(feature);
      }
    });
  }

  const color = shape?.properties?.color || 'blue';
  const showLabel = !!shape?.properties?.showLabel;
  const transparency = shape?.properties?.transparency ?? 0.4;
  const heatmapConfig: HeatmapConfig = shape?.properties?.heatmapConfig;

  const styleFunction = (feature: FeatureLike) => {
    return getFeatureStyle(
      feature,
      color,
      'medium',
      'medium',
      showLabel,
      transparency,
      displayProjection,
      heatmapConfig
    );
  };

  return new VectorLayer({
    source: vectorSource,
    style: styleFunction,
  });
};
